/** Vigia de versão: dá conta de que saiu uma versão nova enquanto este
 *  separador esteve aberto.
 *
 *  Porque é preciso. O service worker vai à rede primeiro nas
 *  navegações, por isso um recarregamento traz sempre a versão nova.
 *  O problema é outro: um separador aberto há horas nunca navega, e
 *  portanto nunca chega a pedir o HTML outra vez. Fica a correr o
 *  código de ontem sem nada que o diga. Aconteceu a 18/09/2026: a
 *  beta.123 estava publicada e o separador aberto mostrava o friso da
 *  beta.122, com os comandos noutros sítios.
 *
 *  Como funciona. O build escreve um `version.json` com a versão (ver
 *  o plugin `versionFile` em vite.config.ts). Aqui pergunta-se por ele
 *  de vez em quando e sempre que a pessoa volta ao separador, e
 *  compara-se com a versão que este código traz compilada. São
 *  diferentes quando houve publicação entretanto.
 *
 *  O pedido leva `cache: 'no-store'`: o ficheiro não é servido pelo
 *  service worker (não é navegação, nem /assets/, nem está na lista de
 *  stale-while-revalidate), mas a cache HTTP do browser podia
 *  responder com a cópia velha e o vigia nunca daria por nada.
 */

import { VERSION } from '@core/release'

/** De quanto em quanto tempo se pergunta, com o separador à vista. */
export const INTERVALO_MS = 15 * 60 * 1000

/** Mínimo entre dois pedidos, para que voltar ao separador de dois em
 *  dois segundos não faça um pedido de cada vez. */
export const MINIMO_ENTRE_PEDIDOS_MS = 60 * 1000

export interface VersionWatchOptions {
  /** A versão que este código traz compilada. */
  versaoAtual?: string
  /** Trocável nos testes. */
  buscar?: (url: string) => Promise<Response>
  agora?: () => number
  /** Chamada uma única vez, quando se descobre uma versão diferente. */
  aoDescobrir: (versaoNova: string) => void
}

/** Lê a versão publicada, ou null se não der para saber.
 *
 *  Qualquer falha devolve null de propósito: sem rede, atrás de um
 *  portal cativo, ou com o ficheiro em falta, o certo é não dizer
 *  nada. Um aviso errado de "há versão nova" é pior do que aviso
 *  nenhum. */
export async function lerVersaoPublicada(
  buscar: (url: string) => Promise<Response> = (url) =>
    fetch(url, { cache: 'no-store' }),
): Promise<string | null> {
  try {
    const url = `${import.meta.env.BASE_URL}version.json`.replace(
      '//version',
      '/version',
    )
    const resposta = await buscar(url)
    if (!resposta.ok) return null
    const corpo: unknown = await resposta.json()
    if (
      typeof corpo === 'object' &&
      corpo !== null &&
      'version' in corpo &&
      typeof (corpo as { version: unknown }).version === 'string'
    ) {
      return (corpo as { version: string }).version
    }
    return null
  } catch {
    return null
  }
}

/** Põe o vigia a correr. Devolve a função que o desliga. */
export function iniciarVersionWatch(opcoes: VersionWatchOptions): () => void {
  const {
    versaoAtual = VERSION,
    buscar,
    agora = () => Date.now(),
    aoDescobrir,
  } = opcoes

  let vivo = true
  let jaAvisou = false
  // -Infinity e não 0: o primeiro pedido tem de passar sempre. Com 0,
  // um relógio que comece perto de zero deixava a primeira verificação
  // dentro da janela do estrangulamento e o vigia nunca perguntava
  // nada.
  let ultimoPedido = -Infinity

  const verificar = async () => {
    if (!vivo || jaAvisou) return
    // Com o separador escondido não se pergunta: a pessoa não está lá
    // para ver o aviso, e volta-se a perguntar mal regresse.
    if (typeof document !== 'undefined' && document.hidden) return
    const t = agora()
    if (t - ultimoPedido < MINIMO_ENTRE_PEDIDOS_MS) return
    ultimoPedido = t

    const publicada = await lerVersaoPublicada(buscar)
    if (!vivo || jaAvisou) return
    if (!publicada || publicada === versaoAtual) return
    jaAvisou = true
    aoDescobrir(publicada)
  }

  const temporizador = setInterval(() => void verificar(), INTERVALO_MS)
  const aoVoltar = () => void verificar()

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', aoVoltar)
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', aoVoltar)
  }

  return () => {
    vivo = false
    clearInterval(temporizador)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', aoVoltar)
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', aoVoltar)
    }
  }
}

/** Recarrega na versão nova.
 *
 *  Antes de recarregar pede-se ao service worker que vá buscar o seu
 *  próprio ficheiro: sem isto, o worker em funções podia continuar a
 *  servir a shell antiga da cache e o recarregamento não mudaria nada.
 *  Se não houver service worker, ou se falhar, recarrega à mesma. */
export async function recarregarNaVersaoNova(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registo = await navigator.serviceWorker.getRegistration()
      await registo?.update()
    }
  } catch {
    // Recarregar é mais importante do que o worker ficar em dia.
  }
  window.location.reload()
}
